import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useMap } from '@wehunt/map'
import type { GeoPt } from '../../lib/types'
import { pinOpacityForFrame } from './printDim'

export function markerClipPolygon(
  corners: readonly { x: number; y: number }[],
  origin: { x: number; y: number },
): string {
  return `polygon(${corners.map((p) => `${(p.x - origin.x).toFixed(2)}px ${(p.y - origin.y).toFixed(2)}px`).join(', ')})`
}

/** DOM markers sit above map layers; clip their overhang to the same sheet. */
export function PrintMarkerClip({
  point,
  frame,
  children,
  testId,
}: {
  point: GeoPt
  frame?: GeoPt[] | null
  children: ReactNode
  testId?: string
}) {
  const map = useMap()
  const ref = useRef<HTMLDivElement>(null)
  const [clipPath, setClipPath] = useState<string | undefined>()
  const opacity = pinOpacityForFrame(point, frame)
  useEffect(() => {
    const element = ref.current
    if (!map || !element || !frame || frame.length < 3 || opacity !== 1) {
      setClipPath(undefined)
      return
    }
    const measure = () => {
      const marker = element.getBoundingClientRect()
      const container = map.getContainer().getBoundingClientRect()
      setClipPath(
        markerClipPolygon(
          frame.map((p) => map.project([p.lon, p.lat])),
          { x: marker.left - container.left, y: marker.top - container.top },
        ),
      )
    }
    map.on('move', measure)
    map.on('resize', measure)
    const resize = new ResizeObserver(measure)
    resize.observe(element)
    measure()
    return () => {
      map.off('move', measure)
      map.off('resize', measure)
      resize.disconnect()
    }
  }, [map, frame, opacity])
  return (
    <div ref={ref} data-testid={testId} style={{ opacity, clipPath }}>
      {children}
    </div>
  )
}
